import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import {
  LTLQuote,
  LTLSelectedAccessorial,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import { TransportType } from 'types/QuoteRequest';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';
import { normalizeQuote } from 'utils/ltl/util';

const LTL_QUOTE_START_PATH = '/quote/quickquote/ltl';
const LTL_QUOTE_RESULT_PATH = '/quote/quickquote/ltl/result';

export type LTLQuickQuoteResponseQuoteError = {
  source: string;
  error: string;
};

export type LTLExternalQuoteAssignment = {
  status: 'success' | 'failure';
  quoteId?: string;
  rateOptionId?: number;
  carrierName?: string;
  messages?: string[];
  quoteStatus?: string;
  tradingPartnerIdCarrier?: string;
  contractIdRated?: string;
  contractStrategyId?: string;
  quoteRefNumRated?: string;
};

export enum LTLExternalQuoteErrorLevel {
  Error = 'error',
  Warning = 'warning',
}

export type LTLExternalQuoteError = {
  source: string;
  message: string;
  level: LTLExternalQuoteErrorLevel;
};

export interface LTLQuickQuoteLineItem {
  packageType: number;
  freightClass: string;
  estimatedFreightClass?: string;
  showEstimatedFreightClassMessage?: boolean;
  usingEstimatedFreightClass?: boolean;
  classManuallySelected: boolean;
  handlingUnits: number;
  handlingUnitTypeName?: string;
  totalWeight: number;
  commodityName?: string;
  commodityExternalTmsId?: string;
  commodityDescription: string;
  quantityOfGoods: number;
  length?: number;
  width?: number;
  height?: number;
  cubicFeet?: number;
  cubicMeters?: number;
  poundsPerCubicFeet?: number;
  // this is turvo specific
  nmfcNumber?: string;
}

export interface LTLQuickQuoteAccessorial {
  externalId: number;
  code: string;
  name: string;
}

export enum LTLShiplifyDecision {
  AutoApply = 'auto_apply',
  Suggest = 'suggest',
  Ignore = 'ignore',
}

export enum LTLShiplifyLookupStatus {
  AddressIncomplete = 'address_incomplete',
  ConnectionError = 'connection_error',
  Disabled = 'disabled',
  FailedOpen = 'failed_open',
  InvalidAddress = 'invalid_address',
  NotFound = 'not_found',
  NoRecommendations = 'no_recommendations',
  RateLimited = 'rate_limited',
  Recommendations = 'recommendations',
  Unavailable = 'unavailable',
}

export type LTLShiplifyRecommendation = {
  codes: string[];
  confidence: string;
  accuracy?: number;
  exempt: boolean;
  decision: LTLShiplifyDecision;
};

export type LTLShiplifyLookupMetadata = {
  status: LTLShiplifyLookupStatus;
  shiplifyStatus?: string;
  recommendations?: LTLShiplifyRecommendation[];
  locationTypes?: string[];
  dockAccess?: boolean;
  forklift?: boolean;
  locationId?: string;
  externalUrl?: string;
};

export interface LTLQuickQuoteStop {
  order: number;
  stopType: StopTypes;
  address: {
    name?: string;
    addressLine1?: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  externalTMSID?: string;
  readyTime?: string;
  readyEndTime?: string;
  mustDeliver?: string;
}

export interface LTLQuickQuoteRequest {
  emailId: number;
  threadId: string;
  quoteRequestId: number;
  transportType: TransportType;
  customerExternalTmsId: string;
  stops: LTLQuickQuoteStop[];
  customer: any;
  lineItems: LTLQuickQuoteLineItem[];
  accessorials: LTLSelectedAccessorial[];
  weightUnit: number;
  dimensionUnit: number;
  searchByTariff: boolean;
  stackable: boolean;
  isFrontOffice: boolean;
  shipmentType: number;
  actualShipmentType: number;
  trailerType: number;
  pcmilerRoutingType: number;
  declaredValue: string;
  deductibleValue: number;
  linearFeet?: number;
  // Turvo: service type string (e.g. "any", "overnight-am"). BE maps to Turvo {id, key, value}.
  transportationType?: string;
  //Turvo: payment term string (e.g. "prepaid", "collect", "third-party"). BE maps to Turvo {id, key, value}.
  paymentTerm?: string;
  // Turvo: pickup services selected in FE as text values; BE maps to Turvo {id,key,value}.
  pickupServices?: string[];
  // Turvo: delivery services selected in FE as text values; BE maps to Turvo {id,key,value}.
  deliveryServices?: string[];
  shiplifyLookups?: Partial<Record<StopTypes, LTLShiplifyLookupMetadata>>;
  // same vocabulary as create_suggestion / FTL quick quote
  sourceCategory?: string;
  source?: string;
  sourceExternalID?: string;
  sourceURL?: string;
  sourceHost?: string;
}

export type LTLQuickQuoteResponse = {
  quotes: LTLQuote[];
  quoteErrors?: LTLQuickQuoteResponseQuoteError[];
  quoteReplyDraftTemplate?: {
    subject: string;
    body: string;
  };
  externalQuoteURL?: string;
  externalQuoteID?: string;
  externalQuoteSource?: string;
  externalQuoteLabel?: string;
  externalQuoteAssignment?: LTLExternalQuoteAssignment;
  externalQuoteError?: LTLExternalQuoteError;
};

//Response from POST /quote/quickquote/ltl when BE runs async; contains jobId for polling.
export type LTLQuickQuoteStartResponse = {
  jobId: string;
  quoteRequestId?: number;
  status?: string;
};

// Request body for POST /quote/quickquote/ltl/result (poll for quote result).
export interface LTLQuickQuoteResultRequest {
  jobId: string;
  stops: LTLQuickQuoteStop[];
}

export async function getLTLQuickQuotes(
  request: LTLQuickQuoteRequest
): Promise<Result<LTLQuickQuoteStartResponse, ApiError>> {
  try {
    const resp = await axios.post<LTLQuickQuoteStartResponse>(
      LTL_QUOTE_START_PATH,
      request
    );

    const jobId = resp.data?.jobId ?? '';

    if (!jobId) {
      return err({ message: 'LTL quote start response missing jobId' });
    }

    // quoteRequestId is the one the job runs under; for a manual rate (0) the server
    // creates it, and Batch LTL keeps it for the shipment the rep added.
    return ok({ jobId, quoteRequestId: resp.data?.quoteRequestId });
  } catch (error) {
    captureException(error, { functionName: 'getLTLQuickQuotes' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to get LTL quotes' });
    }

    if (error.response?.status === 401) {
      throw error;
    }

    if (error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err(toApiError(error, 'Failed to get LTL quotes'));
  }
}

export async function getLTLQuickQuoteResult(
  request: LTLQuickQuoteResultRequest
): Promise<Result<LTLQuickQuoteResponse, ApiError>> {
  try {
    const resp = await axios.post<LTLQuickQuoteResponse>(
      LTL_QUOTE_RESULT_PATH,
      request
    );

    if (resp.status === 202) {
      return err({ message: 'LTL quote still processing', status: 202 });
    }

    const quotes = (resp.data?.quotes ?? []).map((quote: LTLQuote) =>
      normalizeQuote(quote)
    );

    return ok({
      quotes,
      quoteErrors: resp.data?.quoteErrors ?? [],
      quoteReplyDraftTemplate: resp.data?.quoteReplyDraftTemplate,
      externalQuoteURL: resp.data?.externalQuoteURL,
      externalQuoteID: resp.data?.externalQuoteID,
      externalQuoteSource: resp.data?.externalQuoteSource,
      externalQuoteLabel: resp.data?.externalQuoteLabel,
      externalQuoteAssignment: resp.data?.externalQuoteAssignment,
      externalQuoteError: resp.data?.externalQuoteError,
    });
  } catch (error) {
    if (!isAxiosError(error)) {
      captureException(error, { functionName: 'getLTLQuickQuoteResult' });
      return err({ message: 'Failed to get LTL quote result' });
    }

    if (error.response?.status === 401) {
      throw error;
    }

    if (error.message === 'Extension context invalidated.') {
      throw error;
    }

    // 404 = result not ready yet; don't report as exception (expected during polling)
    if (error.response?.status !== 404) {
      captureException(error, { functionName: 'getLTLQuickQuoteResult' });
    }

    return err(
      toApiError(
        error,
        'Failed to get LTL quote result',
        error.response?.status
      )
    );
  }
}

function toApiError(
  error: any,
  fallbackMessage: string,
  status?: number
): ApiError {
  const responseData = error?.response?.data;
  const message =
    responseData?.message || responseData?.error || fallbackMessage;

  return {
    message,
    status,
    level: responseData?.level || responseData?.errorLevel,
  };
}
